/*
 * Panel template for all C2C Connections view within project context
 */
import type {
  C2CConnectionsPanelArea,
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
} from '../types';
import {
  DETAILS_DEFAULT_WIDTH,
  DETAILS_MIN_WIDTH,
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
} from './constants';
import TEXT from './generated/TEXT/c2cConnections';

TEXT.DEFINE({
  directoryNav: {
    accessibility: 'Toggle the Project Navigation',
    hide: 'Hide Project Navigation',
    show: 'Show Project Navigation',
  },
  directoryContent: {
    accessibility: 'Toggle the C2C Connections Directory',
    hide: 'Hide the C2C Connections',
    show: 'Show the C2C connections',
  },
  details: {
    accessibility: 'Toggle the Device Properties',
    hide: 'Hide Device Properties',
    show: 'Show Device Properties',
  },
});

/**
 * Base template (Mobile +). At mobile template we disable all panel
 * architecture except the main panel.
 */
export function baseTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['directoryContent']],
    resizers: {
      directoryContent: 'right',
    },
    columns: [
      /* C2C DirectoryPanel */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        showing: true,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: DETAILS_DEFAULT_WIDTH,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'details']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: MEDIUM_NAV_MIN_WIDTH,
        defaultSize: MEDIUM_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        isToggleable: false,
        showing: true,
      },

      /* Settings Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_MIN_WIDTH,
        defaultMax: 400,
        panelToggle: {
          label: TEXT.details,
          variant: 'right',
        },
        isToggleable: true,
        showing: visible.includes('details'),
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'details']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: MEDIUM_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 208,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        showing: true,
        isToggleable: false,
      },

      /* Settings Panel */
      {
        key: 'details',
        defaultMin: DETAILS_MIN_WIDTH,
        defaultSize: DETAILS_DEFAULT_WIDTH,
        defaultMax: 600,
        isToggleable: true,
        panelToggle: {
          label: TEXT.details,
          variant: 'right',
        },
        showing: visible.includes('details'),
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<C2CConnectionsPanelArea> => {
  return ['directoryNav', 'directoryContent', 'details'];
};

export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
